import { STORAGE_KEYS } from '@/constants/storage';

export type OnboardingModule = 'POS' | 'CRM' | 'HR' | 'FINANCE' | 'PROJECTS' | 'DESK' | 'SOCIAL';
export type OnboardingPlan = 'FREE' | 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE' | 'CUSTOM';

export interface OnboardingState {
  step: number;
  intentModule?: OnboardingModule;
  selectedModules: OnboardingModule[];
  selectedPlan: OnboardingPlan;
}

const DEFAULT_STATE: OnboardingState = {
  step: 0,
  selectedModules: ['POS'],
  selectedPlan: 'FREE',
};

export function readOnboardingState(): OnboardingState {
  const raw = localStorage.getItem(STORAGE_KEYS.ONBOARDING_STATE);
  if (!raw) return DEFAULT_STATE;
  try {
    return { ...DEFAULT_STATE, ...(JSON.parse(raw) as Partial<OnboardingState>) };
  } catch {
    return DEFAULT_STATE;
  }
}

export function writeOnboardingState(state: OnboardingState) {
  localStorage.setItem(STORAGE_KEYS.ONBOARDING_STATE, JSON.stringify(state));
}

export function setOnboardingDone(done: boolean) {
  localStorage.setItem(STORAGE_KEYS.ONBOARDING_DONE, done ? '1' : '0');
}

export type OnboardingFlow = 'fattah-one' | 'module-direct';

/** New org via /register — choose products, then org setup. */
export function requireFattahOneOnboarding() {
  setOnboardingDone(false);
  clearProductSelectionDone();
  localStorage.setItem(STORAGE_KEYS.ONBOARDING_FLOW, 'fattah-one');
}

/** @deprecated Use {@link requireFattahOneOnboarding} */
export function requirePlatformOnboarding() {
  requireFattahOneOnboarding();
}

/** Returning sign-in or direct app (POS, CRM, …) signup — skip platform onboarding. */
export function clearPlatformOnboardingRequired() {
  setOnboardingDone(true);
  setProductSelectionDone(true);
  localStorage.removeItem(STORAGE_KEYS.ONBOARDING_FLOW);
}

export function getOnboardingFlow(): OnboardingFlow | null {
  const raw = localStorage.getItem(STORAGE_KEYS.ONBOARDING_FLOW);
  return raw === 'fattah-one' || raw === 'module-direct' ? raw : null;
}

/** Multi-product picker — only after Fattah One registration, not direct app entry. */
export function isFattahOneProductSelectionRequired(): boolean {
  return (
    isPlatformOnboardingRequired() &&
    !isProductSelectionDone() &&
    getOnboardingFlow() === 'fattah-one'
  );
}

export function isPlatformOnboardingRequired(): boolean {
  return localStorage.getItem(STORAGE_KEYS.ONBOARDING_DONE) === '0';
}

/** True when onboarding is not required (default for existing users). */
export function isOnboardingDone(): boolean {
  return !isPlatformOnboardingRequired();
}

export function setProductSelectionDone(done: boolean) {
  localStorage.setItem(STORAGE_KEYS.ONBOARDING_PRODUCTS_DONE, done ? '1' : '0');
}

export function isProductSelectionDone(): boolean {
  return localStorage.getItem(STORAGE_KEYS.ONBOARDING_PRODUCTS_DONE) === '1';
}

export function clearProductSelectionDone() {
  localStorage.removeItem(STORAGE_KEYS.ONBOARDING_PRODUCTS_DONE);
}

export function resolveModuleIntent(search: string): OnboardingModule | undefined {
  const params = new URLSearchParams(search);
  if (params.has('pos')) return 'POS';
  if (params.has('crm')) return 'CRM';
  if (params.has('hr')) return 'HR';
  if (params.has('finance')) return 'FINANCE';
  const module = params.get('module')?.toUpperCase();
  if (!module) return undefined;
  if (
    module === 'POS' ||
    module === 'CRM' ||
    module === 'HR' ||
    module === 'FINANCE' ||
    module === 'PROJECTS' ||
    module === 'DESK' ||
    module === 'SOCIAL'
  ) {
    return module;
  }
  return undefined;
}
